import { Head } from "vite-react-ssg"
import { useLocation } from "react-router-dom"
import { SITE_NAME, absoluteImageUrl, absolutePageUrl, formatPageTitle, DEFAULT_OG_IMAGE } from "@/lib/seo"

interface PageMetaProps {
	title: string
	description: string
	/** Ruta dentro de `public/images/` (p. ej. `og/inicio.webp`). */
	image?: string
	/** Ruta canónica; por defecto la ruta actual. */
	path?: string
	/** Si es true, indica a los crawlers que no indexen esta página. */
	noIndex?: boolean
}

/**
 * Metadatos de página y Open Graph (OG + Twitter Card).
 * Usa `Head` de vite-react-ssg para que queden en el HTML pre-renderizado.
 */
export function PageMeta({
	title,
	description,
	image = DEFAULT_OG_IMAGE,
	path,
	noIndex = false
}: PageMetaProps): React.JSX.Element {
	const { pathname } = useLocation()
	const pageTitle = formatPageTitle(title)
	const pageUrl = absolutePageUrl(path ?? pathname)
	const imageUrl = absoluteImageUrl(image)

	return (
		<Head>
			<title>{pageTitle}</title>
			<meta name="description" content={description} />
			{noIndex ? <meta name="robots" content="noindex, nofollow" /> : null}
			<link rel="canonical" href={pageUrl} />
			<meta property="og:type" content="website" />
			<meta property="og:site_name" content={SITE_NAME} />
			<meta property="og:locale" content="es_MX" />
			<meta property="og:title" content={pageTitle} />
			<meta property="og:description" content={description} />
			<meta property="og:url" content={pageUrl} />
			<meta property="og:image" content={imageUrl} />
			<meta property="og:image:alt" content={title} />
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={pageTitle} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={imageUrl} />
		</Head>
	)
}
